import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { includes } from 'lodash-es';

import { IconModule } from '@aw/shared/components/icon';
import { ProgressBarComponent } from '@aw/shared/components/progress-bar';
import { RippleDirective } from '@aw/shared/directives';
import { FileSizePipe } from '@aw/shared/pipes';

@Component({
  standalone: true,
  selector: 'aw-file',
  templateUrl: 'file.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    FileSizePipe,
    IconModule,
    NgTemplateOutlet,
    RippleDirective,
    ProgressBarComponent,
  ],
})
export class FileComponent {
  @Input({ required: true }) name: string;

  @Input() size?: number;

  @Input() extension?: string;

  @Input()
  set removable(_removable: BooleanInput) {
    this._removable = coerceBooleanProperty(_removable);
  }

  get removable(): boolean {
    return this._removable;
  }

  @Input()
  set downloadable(_downloadable: BooleanInput) {
    this._downloadable = coerceBooleanProperty(_downloadable);
  }

  get downloadable(): boolean {
    return this._downloadable;
  }

  @Input()
  set uploading(_uploading: BooleanInput) {
    this._uploading = coerceBooleanProperty(_uploading);
  }

  get uploading(): boolean {
    return this._uploading;
  }

  @Input() uploadProgress = 0;

  @Input() uploadSpeed?: number;

  @Output() readonly download = new EventEmitter<void>();

  @Output() readonly remove = new EventEmitter<void>();

  get formattedExtension(): string {
    if (this.extension) {
      return this.extension;
    }

    return this.getFileExtension(this.name);
  }

  private _downloadable = false;

  private _removable = false;

  private _uploading = false;

  onRemove(event: MouseEvent | TouchEvent): void {
    event.stopPropagation();

    this.remove.emit();
  }

  private readonly getFileExtension = (name: string): string => {
    if (this.hasExtension(name)) {
      const split = name.split('.');

      return split.pop() as string;
    }

    return '';
  };

  private readonly hasExtension = (name: string): boolean =>
    includes(name, '.');
}
